import { useRef, useState } from 'react';
import type { ChangeEvent, DragEvent } from 'react';
import { Checkbox, Text } from '@belong/ui';
import { IconCopy, IconTrash } from '@belong/icons';
import Icon, { ICONS } from 'components/Icon/Icon';
import { PaymentPreference } from 'models/enums';
import type { PaymentAccountResourceModel } from 'api/models';
import type {
  ConsumerFailedPaymentModel,
  ConsumerWireInstructionsModel,
} from '../failed-payments.types';
import { FAILED_PAYMENTS_STRINGS } from '../failed-payments-strings';
import {
  formatNegativeFailedPaymentsAmount,
  getBalanceUniqueIds,
  getFailedPaymentsTotalForBalanceIds,
  mapPaymentAccountToPaymentMethodSummary,
} from '../failed-payments-utils';
import { FailedPaymentMethodSelector } from '../failed-payment-method-selector';

const WIRE_RECEIPT_MAX_BYTES = 10 * 1024 * 1024;
const WIRE_RECEIPT_ALLOWED_EXTENSIONS = ['png', 'jpg', 'jpeg', 'pdf'];
const WIRE_RECEIPT_ACCEPT = '.png,.jpg,.jpeg,.pdf';

type WireFileError = {
  filename: string;
  size: string;
  extension: string;
  tooBig: boolean;
  unreadable: boolean;
};

function formatFileSize(bytes: number): string {
  const megabytes = bytes / (1024 * 1024);
  return `${megabytes >= 10 ? Math.round(megabytes) : megabytes.toFixed(1)} MB`;
}

function getWireFileError(file: File): WireFileError | null {
  const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
  const tooBig = file.size > WIRE_RECEIPT_MAX_BYTES;
  const unreadable = !WIRE_RECEIPT_ALLOWED_EXTENSIONS.includes(extension);

  if (!tooBig && !unreadable) return null;

  return {
    filename: file.name,
    size: formatFileSize(file.size),
    extension: `.${extension}`,
    tooBig,
    unreadable,
  };
}

function getWireErrorTitle(error: WireFileError): string {
  const template =
    error.tooBig && error.unreadable
      ? FAILED_PAYMENTS_STRINGS['wire.upload_error_too_big_unreadable']
      : error.tooBig
        ? FAILED_PAYMENTS_STRINGS['wire.upload_error_too_big']
        : FAILED_PAYMENTS_STRINGS['wire.upload_error_unreadable'];

  return template
    .replace('{filename}', error.filename)
    .replace('{size}', error.size)
    .replace('{extension}', error.extension);
}

type HomeownerChangePaymentMethodSubsectionProps = {
  failedPayments: ConsumerFailedPaymentModel[];
  paymentMethods: PaymentAccountResourceModel[];
  selectedPaymentAccountUniqueId: string | null;
  payByWireSelected: boolean;
  onSelectPaymentMethod: (id: string) => void;
  onSelectPayByWire: () => void;
  wireInstructions: ConsumerWireInstructionsModel | null;
  wireInstructionsLoading: boolean;
  wireCoveringBalanceUniqueIds: string[];
  onToggleWireCovering: (balanceUniqueIds: string[]) => void;
  wireReceiptFile: File | null;
  onWireReceiptChange: (file: File | null) => void;
};

export function HomeownerChangePaymentMethodSubsection({
  failedPayments,
  paymentMethods,
  selectedPaymentAccountUniqueId,
  payByWireSelected,
  onSelectPaymentMethod,
  onSelectPayByWire,
  wireInstructions,
  wireInstructionsLoading,
  wireCoveringBalanceUniqueIds,
  onToggleWireCovering,
  wireReceiptFile,
  onWireReceiptChange,
}: HomeownerChangePaymentMethodSubsectionProps) {
  const selectedPaymentMethod = paymentMethods.find(
    (paymentMethod) =>
      paymentMethod.isVerified &&
      paymentMethod.accountId === selectedPaymentAccountUniqueId,
  );
  const selectedSummary = selectedPaymentMethod
    ? mapPaymentAccountToPaymentMethodSummary(selectedPaymentMethod)
    : null;

  return (
    <div className="flex flex-col gap-2xl">
      <div className="flex flex-col gap-sm">
        <Text variant="h3" fontWeight="semibold">
          {FAILED_PAYMENTS_STRINGS['change_payment_method.homeowner.prompt']}
        </Text>

        <FailedPaymentMethodSelector
          paymentMethod={selectedSummary}
          selectedPaymentAccountUniqueId={selectedPaymentAccountUniqueId}
          paymentType={PaymentPreference.Maintenance}
          onSelectPaymentMethod={onSelectPaymentMethod}
          wireTransferSelected={payByWireSelected}
          onSelectWireTransfer={onSelectPayByWire}
        />
      </div>

      {payByWireSelected && (
        <WireDetails
          failedPayments={failedPayments}
          wireInstructions={wireInstructions}
          wireInstructionsLoading={wireInstructionsLoading}
          wireCoveringBalanceUniqueIds={wireCoveringBalanceUniqueIds}
          onToggleWireCovering={onToggleWireCovering}
          wireReceiptFile={wireReceiptFile}
          onWireReceiptChange={onWireReceiptChange}
        />
      )}
    </div>
  );
}

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  }

  return (
    <div className="relative inline-flex">
      <button
        type="button"
        aria-label="Copy to clipboard"
        onClick={handleCopy}
        className="cursor-pointer border-0 bg-transparent p-0"
      >
        <IconCopy
          width={16}
          height={16}
          className={copied ? 'text-dark-green' : 'text-green'}
        />
      </button>
      {copied && (
        <span
          role="status"
          className="absolute bottom-full left-1/2 mb-2xs -translate-x-1/2 whitespace-nowrap rounded-small bg-navy px-xs py-2xs text-p1 font-semibold text-white"
        >
          {FAILED_PAYMENTS_STRINGS['wire.copied']}
        </span>
      )}
    </div>
  );
}

export function WireDetails({
  failedPayments,
  wireInstructions,
  wireInstructionsLoading,
  wireCoveringBalanceUniqueIds,
  onToggleWireCovering,
  wireReceiptFile,
  onWireReceiptChange,
}: Pick<
  HomeownerChangePaymentMethodSubsectionProps,
  | 'failedPayments'
  | 'wireInstructions'
  | 'wireInstructionsLoading'
  | 'wireCoveringBalanceUniqueIds'
  | 'onToggleWireCovering'
  | 'wireReceiptFile'
  | 'onWireReceiptChange'
>) {
  const [fileError, setFileError] = useState<WireFileError | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  function handleFile(file: File | undefined) {
    if (!file) return;
    const error = getWireFileError(file);
    setFileError(error);
    onWireReceiptChange(error ? null : file);
  }

  function handleInputChange(event: ChangeEvent<HTMLInputElement>) {
    handleFile(event.target.files?.[0]);
    event.target.value = '';
  }

  function handleDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    handleFile(event.dataTransfer.files?.[0]);
  }

  const bankRows = wireInstructions
    ? ([
        [
          FAILED_PAYMENTS_STRINGS['wire.account_holder'],
          wireInstructions.accountHolder,
        ],
        [FAILED_PAYMENTS_STRINGS['wire.bank'], wireInstructions.bankName],
        [
          FAILED_PAYMENTS_STRINGS['wire.bank_address'],
          wireInstructions.bankAddress,
        ],
        [
          FAILED_PAYMENTS_STRINGS['wire.routing_number'],
          wireInstructions.routingNumber,
        ],
        [
          FAILED_PAYMENTS_STRINGS['wire.account_number'],
          wireInstructions.accountNumber,
        ],
      ] as const)
    : [];

  const coveredTotal = getFailedPaymentsTotalForBalanceIds(
    failedPayments,
    wireCoveringBalanceUniqueIds,
  );

  return (
    <>
      <div className="flex flex-col gap-sm">
        <Text variant="h3" fontWeight="semibold">
          {FAILED_PAYMENTS_STRINGS['wire.where_title']}
        </Text>

        {wireInstructionsLoading ? (
          <Text variant="body" className="text-navy">
            {FAILED_PAYMENTS_STRINGS['unable_to_pay.partial.preview_loading']}
          </Text>
        ) : wireInstructions ? (
          <div className="flex flex-col">
            <div className="-mb-px flex items-center justify-between rounded-t border border-solid border-gray bg-light-gray p-sm">
              <div className="flex flex-col gap-xs">
                <Text fontWeight="semibold" className="text-navy">
                  {FAILED_PAYMENTS_STRINGS['wire.reference_code']}
                </Text>
                <Text variant="h2" fontWeight="semibold" className="text-green">
                  {wireInstructions.referenceCode}
                </Text>
                <Text variant="p1" className="text-navy">
                  {FAILED_PAYMENTS_STRINGS['wire.reference_hint']}
                </Text>
              </div>
              <CopyButton value={wireInstructions.referenceCode} />
            </div>
            <div className="flex flex-col gap-sm rounded-b border border-solid border-gray bg-white p-sm">
              {bankRows.map(([label, value]) => (
                <div
                  key={label}
                  className="flex items-center justify-between gap-md"
                >
                  <Text className="min-w-0 text-navy">
                    <span className="font-semibold">{label}</span> {value}
                  </Text>
                  <CopyButton value={value} />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <Text variant="p1" className="text-red">
            {FAILED_PAYMENTS_STRINGS['error.generic']}
          </Text>
        )}
      </div>

      <div className="flex flex-col gap-sm">
        <Text variant="h3" fontWeight="semibold">
          {FAILED_PAYMENTS_STRINGS['wire.covering_title']}
        </Text>
        <div className="flex flex-col gap-sm">
          {failedPayments.map((failedPayment) => {
            const balanceIds = getBalanceUniqueIds(failedPayment);
            const balanceUniqueId = balanceIds[0];
            if (!balanceUniqueId) return null;
            const isSelected = balanceIds.some((id) =>
              wireCoveringBalanceUniqueIds.includes(id),
            );

            return (
              <div
                key={balanceUniqueId}
                className="flex items-center justify-between gap-md"
              >
                <Checkbox
                  id={`wire-covering-${balanceUniqueId}`}
                  checked={isSelected}
                  label={failedPayment.label ?? ''}
                  onChange={() => onToggleWireCovering(balanceIds)}
                  fontWeight="regular"
                  wrapperClassName="min-w-0"
                  inputContainerClass="shrink-0"
                  labelClassName="block min-w-0 overflow-hidden"
                  labelTextClassName="truncate"
                />
                <Text
                  fontWeight="semibold"
                  className="shrink-0 text-right text-navy"
                >
                  {formatNegativeFailedPaymentsAmount(failedPayment.amount)}
                </Text>
              </div>
            );
          })}
        </div>

        <div className="flex items-center justify-between border-0 border-t border-dashed border-gray pt-sm">
          <Text fontWeight="semibold">
            {FAILED_PAYMENTS_STRINGS['section.total']}
          </Text>
          <Text fontWeight="semibold" className="text-right">
            {formatNegativeFailedPaymentsAmount(coveredTotal)}
          </Text>
        </div>
      </div>

      <div className="flex flex-col gap-sm">
        <Text variant="h3" fontWeight="semibold">
          {FAILED_PAYMENTS_STRINGS['wire.upload_title']}
        </Text>

        <div
          role="button"
          tabIndex={0}
          onClick={() => fileInputRef.current?.click()}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              fileInputRef.current?.click();
            }
          }}
          onDragOver={(event) => event.preventDefault()}
          onDrop={handleDrop}
          className="flex h-[200px] w-full cursor-pointer flex-col items-center justify-center gap-2xs rounded border-2 border-dashed border-gray bg-light-gray"
        >
          <Icon icon={ICONS.UPLOAD_LARGE.DEFAULT} />
          <Text fontWeight="semibold" className="text-center text-navy">
            {FAILED_PAYMENTS_STRINGS['wire.upload_cta']}
          </Text>
          <Text variant="p1" className="text-center text-dark-gray">
            {FAILED_PAYMENTS_STRINGS['wire.upload_hint']}
          </Text>
          <input
            ref={fileInputRef}
            type="file"
            accept={WIRE_RECEIPT_ACCEPT}
            className="hidden"
            onChange={handleInputChange}
          />
        </div>

        {fileError && (
          <div className="flex flex-col gap-2xs rounded border border-solid border-red bg-[rgba(255,86,97,0.04)] p-sm">
            <span className="flex items-center gap-xs">
              <Text fontWeight="semibold" className="text-navy">
                ⚠️ {getWireErrorTitle(fileError)}
              </Text>
            </span>
            <Text variant="p1" className="text-navy">
              {FAILED_PAYMENTS_STRINGS['wire.upload_error_hint']}
            </Text>
          </div>
        )}

        {wireReceiptFile && (
          <div className="flex items-center justify-between gap-md">
            <Text fontWeight="semibold" className="min-w-0 truncate text-navy">
              {wireReceiptFile.name}
            </Text>
            <button
              type="button"
              aria-label="Remove receipt"
              onClick={() => onWireReceiptChange(null)}
              className="cursor-pointer border-0 bg-transparent p-0 text-red"
            >
              <IconTrash width={14} height={18} />
            </button>
          </div>
        )}
      </div>
    </>
  );
}
